import { defer, from, mergeMap, Observable } from 'rxjs';
import { HttpClient, HttpContext } from '@angular/common/http';
import { UrlService } from '../../../environments/urlService';
import { AiHistoryMessage, AiHistoryParamType, AiRequestDto, AiResponseEvent } from '../global-ai/utils';
import { processSseStream } from '../../util/stream/stream-utils';
import { IS_SPINNER_ACTIVE } from '../../util/interceptors';
import { finalize } from 'rxjs/operators';
import { NotImplementedException } from "src/app/util/error/typed-error";

/**
 * Abstract service for handling AI backend communication, including streaming responses
 * and conversation history management.
 *
 * It handles:
 * - Sending streaming requests to AI endpoints
 * - Processing SSE (Server-Sent Events) streams
 * - Fetching and clearing conversation history
 *
 * Extend this class to create specialized backend services for different AI chat components.
 *
 * @example
 * export class GlobalAiBackendService extends AbstractAiBackendService {
 *   constructor(http: HttpClient, urlService: UrlService) {
 *     super(http, urlService, 'global-ai');
 *   }
 *
 *   protected buildHistoryParams(conversationId: string): AiHistoryParamType {
 *     return { conversationId };
 *   }
 * }
 */
export abstract class AbstractAiBackendService {
  protected readonly url: string;

  protected constructor(
    protected http: HttpClient,
    protected urlService: UrlService,
    protected baseEndpoint: string,
  ) {
    this.url = this.urlService.getFmsUrl() + this.baseEndpoint;
  }

  /**
   * IMPORTANT: Abstract method that must be implemented by subclasses to build parameters
   * for history requests based on conversation identifier.
   *
   * Different AI services may use different parameter structures (conversationId, companyId, etc.)
   *
   * @example
   * // Implementation example:
   * protected buildHistoryParams(companyId: number): AiHistoryParamType {
   *   return { companyId };
   * }
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected buildHistoryParams(param: string | number): AiHistoryParamType {
    throw new NotImplementedException('buildHistoryParams method is not implemented');
  }

  sendRequestStreamWithCharts(request: AiRequestDto): Observable<AiResponseEvent> {
    return defer((): Observable<AiResponseEvent> => {
      const controller = new AbortController();

      return from(
        fetch(this.url + '/stream-with-charts', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'text/event-stream',
            'Cache-Control': 'no-cache',
          },
          body: JSON.stringify(request),
          credentials: 'include',
          signal: controller.signal,
        })
      ).pipe(
        mergeMap((response: Response): Observable<AiResponseEvent> => {
          if (!response.ok) {
            throw new Error('Network response was not ok: ' + response.statusText);
          }
          const reader = response.body?.getReader();
          if (!reader) {
            throw new Error('ReadableStream not supported or no response body.');
          }
          const decoder = new TextDecoder();

          return processSseStream(reader, decoder);
        }),
        finalize((): void => {
          controller.abort();
        })
      );
    });
  }

  fetchConversationHistory(param: string | number): Observable<AiHistoryMessage[]> {
    return this.http.get<AiHistoryMessage[]>(this.url + '/chat-history', {
      params: this.buildHistoryParams(param),
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true,
    });
  }

  /** you can pass full convId here, but I usually prefer to pass a part of it and to add user_id on the backend to get the full one */
  clearHistory(param: string | number): Observable<void> {
    return this.http.delete<void>(this.url + '/clear-history', {
      params: this.buildHistoryParams(param),
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true,
    });
  }
}
